/**
 * The envelope's Cashflow forecast tab (TMMAPP-0101; audit 06 § B3, v2 L1540–1640): the shared `ForecastCard`
 * (TMMAPP-0102) on the envelope's one projection (MM-D-53) — KPIs Balance today, Projected balance with its
 * delta over the horizon and Lowest point "{date} · after {event}", and v2's note — then the "Future
 * register" card (`FutureRegister`: the recurring allocations and the plan lines together, each tagged by
 * its served `sourceKind`). The chart and the list read the same response; a drill-down is the same
 * endpoint with a narrower window, never recomputed here. The series table (`PlanTable`) and its sheet and
 * editors stay (D1) under the card. A published row (no series, no plan line) offers no actions.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Paper, Skeleton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Envelope } from '@/api/envelopes';
import { ErrorCodes, isApiError } from '@/api/errors';
import { errorCopy } from '@/api/errorCopy';
import { planLinesApi } from '@/api/planLines';
import { plannedApi } from '@/api/planned';
import type { RegisterRow } from '@/api/projections';
import { ErrorState } from '@/components/ErrorState';
import { MoneyText } from '@/components/MoneyText';
import { ForecastCard, type ForecastKpi } from '@/features/cashflow/ForecastCard';
import { HORIZON_SCHEMA } from '@/features/cashflow/horizon';
import { PlanTable } from '@/features/cashflow/PlanTable';
import { scopeKey, type DrillDown } from '@/features/cashflow/types';
import { useProjection } from '@/features/cashflow/useProjection';
import { useSeriesHost } from '@/features/cashflow/useSeriesHost';
import { plannedErrorKey } from '@/features/planned/plannedErrors';
import { recurringSeriesLink } from '@/features/planned/plannedRoutes';
import { formatCivilDate, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useUrlState } from '@/shared/useUrlState';
import { resolveTokens } from '@/theme/theme';
import { FutureRegister } from './FutureRegister';
import { actionKey } from './futureRows';

interface EnvelopeForecastTabProps {
  envelope: Envelope;
  /** The virtual account OWNER's currency (§14.1). */
  currency: string;
  /** A writer dismisses and restores events and edits series (the owner before TMMAPP-0110). */
  canWrite: boolean;
  onNotify: (message: string) => void;
}

export function EnvelopeForecastTab({
  envelope,
  currency,
  canWrite,
  onNotify,
}: EnvelopeForecastTabProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const [{ h: horizon }] = useUrlState(HORIZON_SCHEMA);
  const scope = useMemo(() => ({ kind: 'envelope' as const, id: envelope.id }), [envelope.id]);
  const key = scopeKey(scope);
  const [drill, setDrill] = useState<DrillDown | null>(null);
  const [prev, setPrev] = useState({ key, horizon });
  if (prev.key !== key || prev.horizon !== horizon) {
    setPrev({ key, horizon });
    if (drill !== null) setDrill(null);
  }
  const { status, error, full, table, fromToday, reload } = useProjection(scope, horizon, drill);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [seriesNames, setSeriesNames] = useState<Record<number, string>>({});

  // The series table names its series (owner only; a failure leaves the names out).
  const loadSeriesNames = useCallback(async () => {
    if (!canWrite) return;
    try {
      const list = await plannedApi.listSeries({ status: 'ACTIVE' });
      setSeriesNames(Object.fromEntries(list.map((s) => [s.id, s.name])));
    } catch {
      // The names stay as they were.
    }
  }, [canWrite]);

  useEffect(() => {
    queueMicrotask(() => {
      void loadSeriesNames();
    });
  }, [loadSeriesNames]);

  // An edit or a split can make a series: its name is read again with the projection.
  const host = useSeriesHost({
    currency,
    onChanged: async () => {
      await Promise.all([reload(), loadSeriesNames()]);
    },
    onNotify,
  });

  async function setSkipped(row: RegisterRow, skip: boolean) {
    setBusyKey(actionKey(row));
    try {
      if (row.kind === 'PLANNED_EXPENSE' && row.planLineId !== null) {
        const originalOn = row.originalOn ?? row.occursOn ?? row.date;
        if (skip) await planLinesApi.dismiss(envelope.id, row.planLineId, originalOn);
        else await planLinesApi.restore(envelope.id, row.planLineId, originalOn);
      } else if (row.seriesId !== null && row.originalOn !== null && row.sliceRef !== null) {
        if (skip) await plannedApi.cancelSlice(row.seriesId, row.originalOn, row.sliceRef);
        else await plannedApi.restoreSlices(row.seriesId, row.originalOn);
      }
      await reload();
    } catch (err) {
      // The shared write-error copy names `CONCURRENT_UPDATE` (the envelope is being moved right now).
      onNotify(errorCopy(t, err, plannedErrorKey(err) ?? 'planned.errorGeneric'));
      if (isApiError(err) && err.code === ErrorCodes.CONCURRENT_UPDATE) await reload();
    } finally {
      setBusyKey(null);
    }
  }

  if (status === 'loading' && full === null) {
    return (
      <Stack spacing={2.5}>
        <Skeleton variant="rounded" height={320} />
        <Skeleton variant="rounded" height={160} />
      </Stack>
    );
  }
  if (status === 'error' && full === null) {
    return (
      <ErrorState
        variant="section"
        error={error}
        what={t('errors.what.cashflow')}
        onRetry={() => void reload()}
      />
    );
  }
  if (full === null) return null;

  const view = table ?? full;
  const own = full.chartSeries[0];
  const startMinor = own?.points[0]?.balanceMinor ?? null;
  const endMinor = full.endMinor;
  const delta = startMinor !== null && endMinor !== null ? endMinor - startMinor : null;
  const lowest = full.minimum;
  const cause = lowest?.cause?.payee ?? null;
  // The envelope projection's own series carries no name: the legend button reads the envelope's.
  const chart = full.chartSeries
    .slice(0, 1)
    .map((series) => ({ ...series, name: envelope.name, color: 'brand' as const }));

  const kpis: ForecastKpi[] = [
    {
      id: 'today',
      label: t('forecast.balanceToday'),
      value: <MoneyText minor={startMinor} currency={currency} variant="figureSm" />,
    },
    {
      id: 'projected',
      label: t('forecast.projected'),
      value: <MoneyText minor={endMinor} currency={currency} variant="figureSm" />,
      ...(delta !== null
        ? {
            note: (
              <Typography
                variant="caption"
                component="span"
                data-delta=""
                sx={{ color: delta < 0 ? tokens.moneyNegative : tokens.moneyPositive }}
              >
                {t('forecast.overHorizon', {
                  delta: formatMoneyMinor(delta, currency, locale, true),
                })}
              </Typography>
            ),
          }
        : {}),
    },
    ...(lowest !== null
      ? [
          {
            id: 'lowest',
            label: t('forecast.lowestPoint'),
            value: <MoneyText minor={lowest.balanceMinor} currency={currency} variant="figureSm" />,
            note:
              cause !== null
                ? t('forecast.lowestCause', {
                    date: formatCivilDate(lowest.date, locale),
                    payee: cause,
                  })
                : formatCivilDate(lowest.date, locale),
          },
        ]
      : []),
  ];

  const hasSeriesRows = view.register.some(
    (row) => row.kind === 'OCCURRENCE' || row.kind === 'SLICE',
  );

  return (
    <>
      <ForecastCard
        label={t('envelope.forecastRegion', { name: envelope.name })}
        fromToday={fromToday}
        from={full.from}
        to={full.to}
        chart={{
          series: chart,
          events: full.events,
          register: full.register,
          currency,
          minimum: null,
          onDrill: setDrill,
        }}
        kpis={kpis}
        note={t('envelope.forecastNote')}
        list={
          <Stack spacing={2.5}>
            <FutureRegister
              rows={view.register}
              from={full.from}
              startMinor={startMinor}
              currency={currency}
              wide={wide}
              canWrite={canWrite}
              busyKey={busyKey}
              onDismiss={(row) => void setSkipped(row, true)}
              onRestore={(row) => void setSkipped(row, false)}
            />
            {hasSeriesRows && (
              <Paper component="section" aria-labelledby="plan-table-title" sx={{ p: '16px 20px' }}>
                <Box sx={{ mb: 1.5 }}>
                  <Typography id="plan-table-title" variant="h3" component="h2">
                    {t('forecast.eventsTitle')}
                  </Typography>
                  <Typography variant="caption" component="p" sx={{ color: tokens.text2 }}>
                    {t('envelope.planTableSub')}
                  </Typography>
                </Box>
                <PlanTable
                  register={view.register}
                  slices={view.slices}
                  currency={currency}
                  // An envelope's forecast is all in the operating currency, its remainder too (TMMAPP-0175).
                  operatingCurrency={currency}
                  owner={canWrite}
                  labels={{
                    envelopeNames: { [envelope.id]: envelope.name },
                    seriesNames,
                    categories: Object.fromEntries(
                      (envelope.categories ?? []).map((c) => [c.id, c.name]),
                    ),
                  }}
                  fullFrom={full.from}
                  fullTo={full.to}
                  drill={drill}
                  onDrill={setDrill}
                  onWiden={() => setDrill(null)}
                  onOpenOccurrence={(seriesId, originalOn) =>
                    void host.openOccurrence(seriesId, originalOn)
                  }
                  onShowSeries={(seriesId) => {
                    const link = recurringSeriesLink(seriesId);
                    void navigate(link.to, link.options);
                  }}
                  onEditFollowing={(seriesId, originalOn) =>
                    void host.openEditor('following', seriesId, originalOn)
                  }
                  onEditSeries={(seriesId) => void host.openEditor('series', seriesId)}
                />
              </Paper>
            )}
          </Stack>
        }
      />
      {host.node}
    </>
  );
}
